import { POSITIVE_RATING_PERCENT, RatingPercent } from 'common/RatingUtilities';
import { ThumbsDownV2, ThumbsUpV2 } from 'src/shared/design-system/Icon';
import { parseDate } from 'src/shared/utils/DateTimeUtilities';
import { minScreen } from 'src/shared/utils/StyledComponentUtils';
import styled from 'styled-components';
import {
  BorderRadius,
  Colors,
  FontSize,
  FontWeight,
  LineHeight,
  ScreenWidth,
  Spacing,
} from 'web-common/src/shared/styles';

export const MenuReviewCards = styled.div`
  display: flex;
  gap: ${Spacing.SINGLE};
  flex-direction: column;

  ${minScreen(ScreenWidth.LARGE)} {
    flex-direction: row;
  }
`;

const MenuReviewCardV2Container = styled.div`
  background-color: ${Colors.WHITE};
  border-radius: ${BorderRadius.M};
  border: 1px solid ${Colors.GRAY_LITE_20};
  flex: 1;
  padding: ${Spacing.SINGLE};
`;

const Name = styled.p`
  font-size: ${FontSize.M};
  font-weight: ${FontWeight.SEMI_BOLD};
  line-height: ${LineHeight.XS};
  margin: 0;
`;

const Date = styled.p`
  color: ${Colors.GRAY_DARK_10};
  font-size: ${FontSize.S};
  font-weight: ${FontWeight.MEDIUM};
  line-height: ${LineHeight.XXS};
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacing.SINGLE};
  align-items: center;
`;

const Comment = styled.p`
  font-size: ${FontSize.S};
  line-height: ${LineHeight.S};
  margin: 0;
`;

const RatingPercentIconAndName = styled.div`
  display: flex;
  gap: ${Spacing.HALF};
  align-items: center;
`;

const StyledRatingPercentIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${Colors.GRAY_LITE_10};
  flex-shrink: 0;
`;

interface MenuReviewCardProps {
  name: string;
  deliveryDate: string;
  comment: string | null;
  timezone: string;
  ratingPercent: RatingPercent | null;
}

const RatingPercentIcon = ({ ratingPercent }: { ratingPercent: RatingPercent | null }): JSX.Element | null => {
  if (ratingPercent === null) {
    return null;
  }

  return (
    <StyledRatingPercentIcon>
      {ratingPercent === POSITIVE_RATING_PERCENT ? <ThumbsUpV2 outline /> : <ThumbsDownV2 outline />}
    </StyledRatingPercentIcon>
  );
};

export const MenuReviewCard = ({
  name,
  deliveryDate,
  comment,
  timezone,
  ratingPercent,
}: MenuReviewCardProps): JSX.Element | null => {
  const dateText = parseDate(deliveryDate, timezone).toFormat('MMM dd');

  return (
    <MenuReviewCardV2Container>
      <TitleContainer>
        <RatingPercentIconAndName>
          <RatingPercentIcon ratingPercent={ratingPercent} />
          <Name>{name}</Name>
        </RatingPercentIconAndName>
        <Date>{dateText}</Date>
      </TitleContainer>
      <Comment>{comment}</Comment>
    </MenuReviewCardV2Container>
  );
};
